import { useCallback, useEffect, useMemo } from 'react'

import { useAuth } from '@/auth/useAuth'
import {
  CirclePermissionType,
  NetworkPermissionType,
} from '@/graphql/api/generated'
import { useOnboardingSkipStore } from '@/stores/onboardingSkipStore'

const MEMBERSHIP_OK = 'ok'

const isCircleManager = (permission?: CirclePermissionType | null) =>
  permission === CirclePermissionType.Owner ||
  permission === CirclePermissionType.Administrator

const isNetworkManager = (permission?: NetworkPermissionType | null) =>
  permission === NetworkPermissionType.Owner ||
  permission === NetworkPermissionType.Administrator

export interface ProfileActivation {
  /** Joined at least one community (circle membership with status "ok"). */
  hasMember: boolean
  /** Owns/administers at least one community. */
  hasAnimator: boolean
  /** Owns/administers at least one network. */
  hasFederator: boolean
  /** At least one profile is genuinely activated. */
  hasAnyProfile: boolean
  /** Has any membership at all (even pending/invited) — covers invited users. */
  hasAnyMembership: boolean
  /** User explicitly skipped the forced onboarding on this device. */
  skipped: boolean
  /** Whether the forced (non-closeable) onboarding dialog must be shown. */
  shouldShowOnboarding: boolean
  markSkipped: () => void
  clearSkip: () => void
}

/**
 * Derives which profiles the current user has activated and whether the forced
 * profile-onboarding gate must be shown. The forced gate only targets brand-new
 * users with zero engagement: any membership (incl. invitations) or an explicit
 * skip releases it. The per-profile booleans drive the (closable) "add a
 * profile" dialog reused later.
 */
export const useProfileActivation = (): ProfileActivation => {
  const auth = useAuth()
  const user = auth.user
  const userId = user?.id ?? null

  const skippedMap = useOnboardingSkipStore((state) => state.skipped)
  const hydrate = useOnboardingSkipStore((state) => state.hydrate)
  const markSkippedInStore = useOnboardingSkipStore(
    (state) => state.markSkipped
  )
  const clearSkipInStore = useOnboardingSkipStore((state) => state.clearSkip)

  // Hydrate from localStorage once per user; the store keeps it reactive across
  // the forced gate and the dialog (separate hook instances).
  useEffect(() => {
    if (userId) {
      hydrate(userId)
    }
  }, [userId, hydrate])

  const skipped = userId ? (skippedMap[userId] ?? false) : false
  // The skip flag is read from localStorage in the effect above, after the first
  // render. Until the user's entry exists in the store, treat skip state as
  // unknown so the forced gate does not flash before hydration completes.
  const skipHydrated = !!userId && userId in skippedMap

  const markSkipped = useCallback(() => {
    if (userId) {
      markSkippedInStore(userId)
    }
  }, [userId, markSkippedInStore])

  const clearSkip = useCallback(() => {
    if (userId) {
      clearSkipInStore(userId)
    }
  }, [userId, clearSkipInStore])

  const { hasMember, hasAnimator, hasFederator, hasAnyMembership } =
    useMemo(() => {
      const circleEdges = user?.circleMemberships?.edges ?? []
      const networkEdges = user?.networkMemberships?.edges ?? []

      const hasMember = circleEdges.some(
        (edge) => edge?.node?.status === MEMBERSHIP_OK
      )
      const hasAnimator = circleEdges.some(
        (edge) =>
          edge?.node?.status === MEMBERSHIP_OK &&
          isCircleManager(edge?.node?.permission)
      )
      const hasFederator = networkEdges.some(
        (edge) =>
          edge?.node?.status === MEMBERSHIP_OK &&
          isNetworkManager(edge?.node?.permission)
      )
      const hasAnyMembership =
        (user?.circleMemberships?.totalCount ?? 0) > 0 ||
        (user?.networkMemberships?.totalCount ?? 0) > 0

      return { hasMember, hasAnimator, hasFederator, hasAnyMembership }
    }, [user])

  const hasAnyProfile = hasMember || hasAnimator || hasFederator
  const shouldShowOnboarding =
    Boolean(user) &&
    skipHydrated &&
    !hasAnyProfile &&
    !hasAnyMembership &&
    !skipped

  return {
    hasMember,
    hasAnimator,
    hasFederator,
    hasAnyProfile,
    hasAnyMembership,
    skipped,
    shouldShowOnboarding,
    markSkipped,
    clearSkip,
  }
}
